import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionFunctionArgs,
  Links,
  LoaderFunctionArgs,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  data,
  isRouteErrorResponse,
  useLoaderData,
  useRouteError,
} from 'react-router';
import { Toaster } from 'sonner';
import { CookieConsentProvider } from '@/components/custom/cookie-consent';
import { assetBaseUrlFromEnv } from '~/lib/assets';
import { parseConsentCookie } from '~/lib/cookie-consent';
import { siteOrigin } from '~/lib/site.server';
import { getLocale, i18nextMiddleware } from '~/middleware/i18next';
import { searchableMiddleware } from '~/middleware/searchable.server';
import { commitSession, getSession } from '~/services/session.server';
import './styles/tailwind.css';

// Outermost on purpose: it should time everything the router does, i18n
// resolution included, and see the status the visitor actually received.
export const middleware = [searchableMiddleware, i18nextMiddleware];

/** GTM container ids look like `GTM-XXXXXXX`; anything else is not interpolated. */
const GTM_ID_PATTERN = /^GTM-[A-Z0-9]{4,}$/;

/** Surfaces that must never reach analytics: the staff console and the
    invitation-only review study. Their traffic is internal and would both skew
    the marketing numbers and follow named reviewers around. */
const UNTRACKED_PREFIXES = ['/admin', '/review', '/healthcheck'];

/**
 * The container id the client may load, or null for none.
 *
 * Three conditions, deliberately: `GTM_ID` must be configured (it is set in
 * fly.toml, so a local checkout has no id and cannot send events), the build must
 * be a production one, and the path must not be an untracked surface. Either of
 * the first two alone is too weak — `NODE_ENV` is also "production" for a local
 * `npm run start`, and an id copied into a local .env would otherwise start
 * reporting dev traffic as real.
 *
 * Passing this check is permission to load *if consented*, not to load. The
 * container is injected by `CookieConsentProvider` once a visitor grants an
 * optional category, never from the document itself.
 */
function resolveGtmId(pathname: string): string | null {
  const id = (process.env.GTM_ID ?? '').trim();
  if (!GTM_ID_PATTERN.test(id)) return null;
  if (process.env.NODE_ENV !== 'production') return null;
  if (UNTRACKED_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))) return null;
  return id;
}

export async function loader({ request, context }: LoaderFunctionArgs) {
  const locale = getLocale(context);
  const url = new URL(request.url);
  // Handed to every route's meta function (via `matches`) so canonical/og:url/
  // og:image can be absolute on the client too, where process.env doesn't exist.
  const siteUrl = siteOrigin();
  // Where /images/* is served from. Handed down the same way as siteUrl so client
  // renders resolve identically to the server ones — see lib/assets.ts.
  const assetBaseUrl = assetBaseUrlFromEnv();
  const gtmId = resolveGtmId(url.pathname);
  // Read here rather than on the client so the first paint already knows the
  // decision: a returning visitor never sees the banner appear and vanish.
  const cookieConsent = parseConsentCookie(request.headers.get('cookie'));

  // Persist only an explicit ?lang= choice. An unconditional write would run
  // with the middleware's pre-action locale during the language-change action's
  // revalidation and clobber the cookie the action just set.
  if (url.searchParams.get('lang')) {
    const session = await getSession(request);
    session.set('language', locale);
    return data(
      { locale, siteUrl, assetBaseUrl, gtmId, cookieConsent },
      { headers: { 'Set-Cookie': await commitSession(session) } }
    );
  }

  return data({ locale, siteUrl, assetBaseUrl, gtmId, cookieConsent });
}

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData();
  const session = await getSession(request);
  const action = formData.get('_action');

  if (action === 'onChangeLanguage') {
    const language = formData.get('language')?.toString() ?? '';
    session.set('language', language);
    return data(null, { headers: { 'Set-Cookie': await commitSession(session) } });
  }
}

export default function App() {
  const { locale, gtmId, cookieConsent } = useLoaderData<typeof loader>();
  const { i18n, t } = useTranslation();

  useEffect(() => {
    if (i18n.language !== locale) i18n.changeLanguage(locale);
  }, [locale, i18n]);

  return (
    <html lang={locale}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content="#f5efe7" />
        {/* og:type / og:site_name are composed per route by app/lib/meta.ts — a
            hardcoded copy here would duplicate rather than be overridden, since
            only descriptors returned from meta functions are deduplicated. */}
        <link rel="icon" href="/favicon.ico" sizes="any" />
        <link rel="apple-touch-icon" href="/apple-touch-icon.png" sizes="180x180" />
        <Meta />
        <Links />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600;700&family=Geist+Mono:wght@400;500&family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;1,400;1,600;1,700&display=swap"
          rel="stylesheet"
        />
      </head>
      <body>
        {/* No <noscript> tag-manager iframe: without JavaScript there is no way to
            ask for consent, and firing it anyway is exactly what consent is for. */}
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-50 focus:rounded-md focus:bg-background focus:px-4 focus:py-2 focus:text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          {t('skipToContent')}
        </a>
        <CookieConsentProvider initialConsent={cookieConsent} gtmId={gtmId}>
          <Outlet />
        </CookieConsentProvider>
        <Toaster position="top-right" richColors closeButton expand={true} toastOptions={{ className: 'font-body' }} />
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export function ErrorBoundary() {
  const error = useRouteError();

  let message = 'Oops! An unexpected error occurred.';
  let details = '';
  let stack: string | undefined;

  const isProduction = process.env.NODE_ENV === 'production';

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? '404' : 'Error';
    details = error.status === 404 ? 'The requested page could not be found.' : error.statusText || details;
  } else if (error && error instanceof Error) {
    if (isProduction) {
      details = 'An unexpected error occurred.';
    } else {
      details = error.message || error.toString();
      stack = error.stack || 'No stack trace available';
    }
  }

  return (
    <html lang="en">
      <head>
        <title>Error</title>
        <meta name="robots" content="noindex, nofollow" />
        <link rel="icon" href="/favicon.ico" sizes="any" />
        <link rel="apple-touch-icon" href="/apple-touch-icon.png" sizes="180x180" />
        <Meta />
        <Links />
      </head>
      <body>
        <main className="flex min-h-screen items-center justify-center bg-background p-6">
          <div className="w-full max-w-2xl rounded-2xl border border-border bg-card p-8 shadow-card-soft">
            <h1 className="font-display text-2xl font-bold text-foreground">{message}</h1>
            {details && <p className="mt-4 text-muted-foreground">{details}</p>}
            {stack && (
              <pre className="mt-6 overflow-x-auto rounded-md bg-muted p-4 text-xs">
                <code>{stack}</code>
              </pre>
            )}
          </div>
        </main>
        <Scripts />
      </body>
    </html>
  );
}
